import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  Paper,
  Typography,
  IconButton,
  Popover,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Autocomplete,
  Divider
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { ChatRoom, ChatMessage } from '../../types/chat.types';
import { useChatStore } from '../../store/useChatStore';
import chatApi from '../../utils/chatApi';
import { MessageItem } from './MessageItem';
import { MessageInput } from './MessageInput';
import { TypingIndicator } from './TypingIndicator';

const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const stringToColor = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = str.charCodeAt(i) + (hash * 31 - hash);
  }
  return `hsl(${Math.abs(hash) % 360}, 65%, 50%)`;
};

interface ChatThreadProps {
  joinRoom: (roomId: string) => void;
  leaveRoom: (roomId: string) => void;
  onParticipantsChanged?: () => void;
  onRoomDeleted?: () => void;
  onRoomRenamed?: (roomId: string, newName: string) => void;
  room: ChatRoom;
  userId: string;
  userLanguage: string;
  users: any[];
}

function ChatThread({
  room,
  userId,
  userLanguage,
  users,
  joinRoom,
  leaveRoom,
  onParticipantsChanged,
  onRoomDeleted,
  onRoomRenamed
}: ChatThreadProps): JSX.Element {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const [participantsAnchor, setParticipantsAnchor] =
    useState<HTMLButtonElement | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLButtonElement | null>(null);

  const [renameOpen, setRenameOpen] = useState(false);
  const [newRoomName, setNewRoomName] = useState('');
  const [renameLoading, setRenameLoading] = useState(false);

  const [addParticipantOpen, setAddParticipantOpen] = useState(false);
  const [selectedAddUser, setSelectedAddUser] = useState<any | null>(null);
  const [addParticipantLoading, setAddParticipantLoading] = useState(false);

  const [removeParticipantOpen, setRemoveParticipantOpen] = useState(false);
  const [selectedRemoveUser, setSelectedRemoveUser] = useState<any | null>(
    null
  );
  const [removeParticipantLoading, setRemoveParticipantLoading] =
    useState(false);

  const [deleteRoomOpen, setDeleteRoomOpen] = useState(false);
  const [deleteRoomLoading, setDeleteRoomLoading] = useState(false);

  const isOwner = (room as any).createdBy === userId && room.type !== 'direct';
  const canDelete = isOwner || room.type === 'direct';

  const { messages, setMessages, typingUsers, clearUnread, setCurrentRoom } =
    useChatStore();
  const roomMessages = messages[room.id] || [];

  const scrollToBottom = (instant = false) => {
    messagesEndRef.current?.scrollIntoView({
      behavior: instant ? 'auto' : 'smooth'
    });
  };

  const markMessagesAsRead = async () => {
    const unreadMessages = roomMessages.filter(
      (msg) =>
        msg.userId !== userId &&
        (!msg.readReceipts ||
          !msg.readReceipts.some((r) => r.userId === userId))
    );
    await Promise.all(
      unreadMessages.map((message) =>
        chatApi.markAsRead(message.id).catch((error: unknown) => {
          console.error('Failed to mark message as read:', error);
        })
      )
    );
  };

  useEffect(() => {
    let cancelled = false;

    const loadAndSetup = async () => {
      if (!room?.id || cancelled) return;

      setIsInitialLoad(true);
      setLoading(true);

      try {
        const data = await chatApi.getMessages(
          room.id,
          undefined,
          userLanguage
        );
        if (cancelled) return;
        setMessages(room.id, data);
        setLoading(false);
        setCurrentRoom(room);
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load messages:', error);
          setLoading(false);
        }
      }

      if (!cancelled) {
        joinRoom(room.id);
        clearUnread(room.id);
      }
    };

    loadAndSetup();

    return () => {
      cancelled = true;
      if (room?.id) {
        leaveRoom(room.id);
        setCurrentRoom(null);
      }
    };
  }, [room.id, userLanguage]);

  useEffect(() => {
    if (roomMessages.length > 0 && !loading) {
      scrollToBottom(isInitialLoad);
      if (isInitialLoad) setIsInitialLoad(false);
    }
  }, [roomMessages.length, loading]);

  useEffect(() => {
    if (roomMessages.length > 0 && !loading) {
      markMessagesAsRead();
    }
  }, [roomMessages.length, loading]);

  const handleSendMessage = async (text: string) => {
    try {
      await chatApi.sendMessage(room.id, {
        messageText: text,
        originalLanguage: userLanguage
      });
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  const handleEditMessage = async (messageId: string, newText: string) => {
    try {
      await chatApi.editMessage(messageId, newText);
    } catch (error) {
      console.error('Failed to edit message:', error);
    }
  };

  const handleDeleteMessage = async (messageId: string) => {
    try {
      await chatApi.deleteMessage(messageId);
    } catch (error) {
      console.error('Failed to delete message:', error);
    }
  };

  const handleRename = async () => {
    if (!newRoomName.trim()) return;
    setRenameLoading(true);
    try {
      await chatApi.renameRoom(room.id, newRoomName.trim());
      onRoomRenamed?.(room.id, newRoomName.trim());
      setRenameOpen(false);
    } catch (error) {
      console.error('Failed to rename room:', error);
    } finally {
      setRenameLoading(false);
    }
  };

  const handleAddParticipant = async () => {
    if (!selectedAddUser) return;
    setAddParticipantLoading(true);
    try {
      await chatApi.addParticipant(room.id, selectedAddUser.id);
      setAddParticipantOpen(false);
      setSelectedAddUser(null);
      onParticipantsChanged?.();
    } catch (error) {
      console.error('Failed to add participant:', error);
    } finally {
      setAddParticipantLoading(false);
    }
  };

  const handleRemoveParticipant = async () => {
    if (!selectedRemoveUser) return;
    setRemoveParticipantLoading(true);
    try {
      await chatApi.removeParticipant(room.id, selectedRemoveUser.id);
      setRemoveParticipantOpen(false);
      setSelectedRemoveUser(null);
      onParticipantsChanged?.();
    } catch (error) {
      console.error('Failed to remove participant:', error);
    } finally {
      setRemoveParticipantLoading(false);
    }
  };

  const handleDeleteRoom = async () => {
    setDeleteRoomLoading(true);
    try {
      await chatApi.deleteRoom(room.id);
      setDeleteRoomOpen(false);
      onRoomDeleted?.();
    } catch (error) {
      console.error('Failed to delete room:', error);
    } finally {
      setDeleteRoomLoading(false);
    }
  };

  const usersNotInRoom = users.filter(
    (u) => u.id !== userId && !room.participantIds?.includes(u.id)
  );

  const removableParticipants = users.filter(
    (u) => u.id !== userId && room.participantIds?.includes(u.id)
  );

  const getParticipantName = (participantId: string): string => {
    if (participantId === userId) return 'You';
    const user = users.find((u) => u.id === participantId);
    if (user) return `${user.firstName} ${user.lastName}`;
    return 'Unknown User';
  };

  if (loading) {
    return (
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          flex: 1,
          justifyContent: 'center'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const currentTyping = typingUsers[room.id] || [];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        position: 'relative'
      }}
    >
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          alignItems: 'center',
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          gap: 1.5,
          left: 0,
          position: 'absolute',
          px: 2,
          py: 1.25,
          right: 0,
          top: 0,
          zIndex: 10
        }}
      >
        {/* Room avatar — IconButton so anchor type matches Popover expectation */}
        <IconButton
          disableRipple
          onClick={(e) => setParticipantsAnchor(e.currentTarget)}
          sx={{
            '&:hover': {
              bgcolor:
                room.type === 'direct'
                  ? stringToColor(room.name)
                  : 'primary.dark'
            },
            alignItems: 'center',
            bgcolor:
              room.type === 'direct'
                ? stringToColor(room.name)
                : 'primary.main',
            borderRadius: '50%',
            color: 'white',
            cursor: 'pointer',
            display: 'flex',
            flexShrink: 0,
            fontSize: '0.8rem',
            fontWeight: 700,
            height: 36,
            justifyContent: 'center',
            p: 0,
            width: 36
          }}
        >
          {getInitials(room.name)}
        </IconButton>

        {/* Name + subtitle */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: '0.9375rem',
              fontWeight: 600,
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {room.name}
          </Typography>
          <Typography
            sx={{ color: 'text.disabled', fontSize: '0.72rem', mt: '1px' }}
          >
            {room.participantIds?.length || 0} members
          </Typography>
        </Box>

        {(isOwner || canDelete) && (
          <IconButton
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            size="small"
          >
            <MoreVertIcon />
          </IconButton>
        )}
      </Paper>

      {/* Owner actions menu */}
      <Menu
        anchorEl={menuAnchor}
        onClose={() => setMenuAnchor(null)}
        open={Boolean(menuAnchor)}
      >
        {isOwner && (
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              setNewRoomName(room.name);
              setRenameOpen(true);
            }}
          >
            <EditIcon fontSize="small" sx={{ mr: 1 }} /> Rename
          </MenuItem>
        )}
        {isOwner && (
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              setAddParticipantOpen(true);
            }}
          >
            <PersonAddIcon fontSize="small" sx={{ mr: 1 }} /> Add Participant
          </MenuItem>
        )}
        {isOwner && (
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              setRemoveParticipantOpen(true);
            }}
          >
            <PersonRemoveIcon fontSize="small" sx={{ mr: 1 }} /> Remove
            Participant
          </MenuItem>
        )}
        {isOwner && <Divider />}
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            setDeleteRoomOpen(true);
          }}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete{' '}
          {room.type === 'direct' ? 'Conversation' : 'Room'}
        </MenuItem>
      </Menu>

      {/* Participants popover */}
      <Popover
        anchorEl={participantsAnchor}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        onClose={() => setParticipantsAnchor(null)}
        open={Boolean(participantsAnchor)}
      >
        <Box sx={{ minWidth: 200, p: 2 }}>
          <Typography sx={{ mb: 1 }} variant="heading_300">
            Members ({room.participantIds?.length || 0})
          </Typography>
          <List dense>
            {room.participantIds?.map((participantId) => (
              <ListItem key={participantId}>
                <ListItemText
                  primary={getParticipantName(participantId)}
                  secondary={
                    participantId !== userId
                      ? users.find((u) => u.id === participantId)?.email
                      : null
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Popover>

      {/* Messages */}
      <Box
        ref={messagesContainerRef}
        sx={{
          bgcolor: 'surface.200',
          bottom: 0,
          left: 0,
          overflow: 'auto',
          pb: '112px',
          position: 'absolute',
          pt: '72px',
          px: 2,
          right: 0,
          top: 0
        }}
      >
        {roomMessages.length === 0 ? (
          <Box
            sx={{
              alignItems: 'center',
              color: 'secondary.400',
              display: 'flex',
              height: '100%',
              justifyContent: 'center'
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Box
                component="span"
                sx={{ display: 'block', fontSize: 14, mb: 1 }}
              >
                No messages yet
              </Box>
              <Box component="span" sx={{ fontSize: 12 }}>
                Start the conversation!
              </Box>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {roomMessages.map((message: ChatMessage, index: number) => {
              const prev = roomMessages[index - 1];
              const next = roomMessages[index + 1];

              const sameUserAsPrev = prev && prev.userId === message.userId;
              const sameUserAsNext = next && next.userId === message.userId;

              const timeSincePrev = prev
                ? new Date(message.createdAt).getTime() -
                  new Date(prev.createdAt).getTime()
                : Infinity;
              const timeToNext = next
                ? new Date(next.createdAt).getTime() -
                  new Date(message.createdAt).getTime()
                : Infinity;

              const isContinuation = sameUserAsPrev && timeSincePrev < 300000; // 5 min
              const showAvatar = !isContinuation;
              const showTimestamp = !sameUserAsNext || timeToNext > 300000;

              // Date divider
              const showDateDivider =
                index === 0 ||
                new Date(message.createdAt).toDateString() !==
                  new Date(prev!.createdAt).toDateString();

              const formatDateLabel = (date: Date): string => {
                const today = new Date();
                const yesterday = new Date(today);
                yesterday.setDate(today.getDate() - 1);
                if (date.toDateString() === today.toDateString())
                  return 'Today';
                if (date.toDateString() === yesterday.toDateString())
                  return 'Yesterday';
                return date.toLocaleDateString(undefined, {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                });
              };

              return (
                <React.Fragment key={message.id}>
                  {showDateDivider && (
                    <Box
                      sx={{
                        alignItems: 'center',
                        display: 'flex',
                        gap: 1.5,
                        my: 2
                      }}
                    >
                      <Box
                        sx={{ bgcolor: 'divider', flex: 1, height: '1px' }}
                      />
                      <Typography
                        sx={{
                          color: 'text.disabled',
                          fontSize: '0.68rem',
                          fontWeight: 600,
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase'
                        }}
                      >
                        {formatDateLabel(new Date(message.createdAt))}
                      </Typography>
                      <Box
                        sx={{ bgcolor: 'divider', flex: 1, height: '1px' }}
                      />
                    </Box>
                  )}
                  <MessageItem
                    isContinuation={isContinuation}
                    isOwn={message.userId === userId}
                    message={message}
                    onDelete={handleDeleteMessage}
                    onEdit={handleEditMessage}
                    showAvatar={showAvatar}
                    showTimestamp={showTimestamp}
                    user={users.find((u: any) => u.id === message.userId)}
                  />
                </React.Fragment>
              );
            })}
            <div ref={messagesEndRef} />
          </Box>
        )}
      </Box>

      {/* Message input */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          bottom: 0,
          left: 0,
          position: 'absolute',
          right: 0,
          zIndex: 10
        }}
      >
        {currentTyping.length > 0 && (
          <Box sx={{ pt: 1, px: 2 }}>
            <TypingIndicator users={currentTyping} />
          </Box>
        )}
        <MessageInput
          onSend={handleSendMessage}
          roomId={room.id}
          userId={userId}
        />
      </Box>

      {/* Rename dialog */}
      <Dialog
        fullWidth
        maxWidth="xs"
        onClose={() => setRenameOpen(false)}
        open={renameOpen}
      >
        <DialogTitle>Rename Room</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Room Name"
            onChange={(e) => setNewRoomName(e.target.value)}
            sx={{ mt: 1 }}
            value={newRoomName}
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={renameLoading} onClick={() => setRenameOpen(false)}>
            Cancel
          </Button>
          <Button
            disabled={renameLoading || !newRoomName.trim()}
            onClick={handleRename}
            variant="contained"
          >
            {renameLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add participant dialog */}
      <Dialog
        fullWidth
        maxWidth="xs"
        onClose={() => setAddParticipantOpen(false)}
        open={addParticipantOpen}
      >
        <DialogTitle>Add Participant</DialogTitle>
        <DialogContent>
          <Autocomplete
            getOptionLabel={(option) =>
              `${option.firstName} ${option.lastName} (${option.email})`
            }
            onChange={(_, value) => setSelectedAddUser(value)}
            options={usersNotInRoom}
            renderInput={(params) => (
              // eslint-disable-next-line react/jsx-props-no-spreading
              <TextField {...params} label="Select User" sx={{ mt: 1 }} />
            )}
            value={selectedAddUser}
          />
        </DialogContent>
        <DialogActions>
          <Button
            disabled={addParticipantLoading}
            onClick={() => setAddParticipantOpen(false)}
          >
            Cancel
          </Button>
          <Button
            disabled={addParticipantLoading || !selectedAddUser}
            onClick={handleAddParticipant}
            variant="contained"
          >
            {addParticipantLoading ? 'Adding...' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove participant dialog */}
      <Dialog
        fullWidth
        maxWidth="xs"
        onClose={() => setRemoveParticipantOpen(false)}
        open={removeParticipantOpen}
      >
        <DialogTitle>Remove Participant</DialogTitle>
        <DialogContent>
          <Autocomplete
            getOptionLabel={(option) =>
              `${option.firstName} ${option.lastName} (${option.email})`
            }
            onChange={(_, value) => setSelectedRemoveUser(value)}
            options={removableParticipants}
            renderInput={(params) => (
              // eslint-disable-next-line react/jsx-props-no-spreading
              <TextField {...params} label="Select User" sx={{ mt: 1 }} />
            )}
            value={selectedRemoveUser}
          />
        </DialogContent>
        <DialogActions>
          <Button
            disabled={removeParticipantLoading}
            onClick={() => setRemoveParticipantOpen(false)}
          >
            Cancel
          </Button>
          <Button
            color="error"
            disabled={removeParticipantLoading || !selectedRemoveUser}
            onClick={handleRemoveParticipant}
            variant="contained"
          >
            {removeParticipantLoading ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete room dialog */}
      <Dialog
        fullWidth
        maxWidth="xs"
        onClose={() => setDeleteRoomOpen(false)}
        open={deleteRoomOpen}
      >
        <DialogTitle>Delete Room</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{room.name}</strong>? This
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={deleteRoomLoading}
            onClick={() => setDeleteRoomOpen(false)}
          >
            Cancel
          </Button>
          <Button
            color="error"
            disabled={deleteRoomLoading}
            onClick={handleDeleteRoom}
            variant="contained"
          >
            {deleteRoomLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

ChatThread.defaultProps = {
  onParticipantsChanged: undefined,
  onRoomDeleted: undefined,
  onRoomRenamed: undefined
};

export default ChatThread;
